/**
 * Geofence Events Page
 * Visar alla geofence-händelser (in/ut-passage) med plats, tid och kostnad
 */

import { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import {
 ArrowLeftIcon,
 ClockIcon,
 CurrencyDollarIcon,
 FunnelIcon,
 ArrowPathIcon,
 MapPinIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

import DateInput from '@/components/common/DateInput';

interface GeofenceEvent {
 _id?: string;
 event_id: string;
 geofence_id: string;
 geofence_name: string;
 vehicle_id: string;
 vehicle_registration?: string | null;
 driver_id?: string;
 organization_id: string;
 trip_id?: string;
 event_type: 'enter' | 'exit';
 location: {
 type: 'Point';
 coordinates: [number, number]; // [lng, lat]
 };
 timestamp: string;
 duration_inside?: number;
 cost?: number;
 speed?: number;
 heading?: number;
 coordinates_filtered?: boolean;
}

interface GeofenceEventsResponse {
 events: GeofenceEvent[];
 total: number;
 page: number;
 pageSize: number;
 totalPages: number;
 totalCost: number;
 enterCount: number;
 exitCount: number;
}

const GeofenceEvents = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [events, setEvents] = useState<GeofenceEvent[]>([]);
 const [loading, setLoading] = useState(false);
 const [filters, setFilters] = useState({
 vehicle_id: '',
 geofence_id: '',
 event_type: '' as '' | 'enter' | 'exit',
 from_date: '',
 to_date: '',
 });
 const [pagination, setPagination] = useState({
 page: 1,
 pageSize: 50,
 total: 0,
 totalPages: 0,
 });
 const [geofences, setGeofences] = useState<Array<{ geofence_id: string; name: string; is_toll?: boolean; scope?: string }>>([]);
 const [vehicles, setVehicles] = useState<Array<{ id: string; name: string; registration_number?: string }>>([]);
 const [geofenceSearch, setGeofenceSearch] = useState('');
 const [vehicleSearch, setVehicleSearch] = useState('');
 const [stats, setStats] = useState({ totalCost: 0, enterCount: 0, exitCount: 0 });

 // Ladda vehicles och geofences en gång vid mount
 useEffect(() => {
 loadGeofences();
 loadVehicles();
 }, []);

 // Ladda events när filters eller pagination ändras
 useEffect(() => {
 loadEvents();
 }, [filters, pagination.page, pagination.pageSize]);

 const loadEvents = async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams();
 if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
 if (filters.geofence_id) params.append('geofence_id', filters.geofence_id);
 if (filters.event_type) params.append('event_type', filters.event_type);
 if (filters.from_date) params.append('from_date', filters.from_date);
 if (filters.to_date) params.append('to_date', filters.to_date);
 params.append('page', pagination.page.toString());
 params.append('pageSize', pagination.pageSize.toString());

 const url = `/api/geofences/events?${params.toString()}`;
 const response = await api.get<{ success: boolean; data: GeofenceEventsResponse }>(url);

 if (response.data.success) {
 const data = response.data.data;
 
 setEvents(data.events);
 setPagination({
 ...pagination,
 total: data.total,
 totalPages: data.totalPages,
 });
 // Spara statistik från alla events (inte bara synliga)
 setStats({
 totalCost: data.totalCost || 0,
 enterCount: data.enterCount || 0,
 exitCount: data.exitCount || 0,
 });
 }
 } catch (error: any) {
 if (error.response?.status === 404) {
 toast.error(t('geofences.events.error404'));
 } else if (error.response?.status === 401) {
 toast.error(t('geofences.events.error401'));
 } else {
 toast.error(t('geofences.events.error'));
 }
 } finally {
 setLoading(false);
 }
 };

 const loadGeofences = async () => {
 try {
 const response = await api.get('/api/geofences');
 if (response.data.success) {
 // Sortera: Tullområden först, sedan alfabetiskt
 const sorted = response.data.data
 .map((g: any) => ({
 geofence_id: g.geofence_id,
 name: g.name,
 is_toll: g.is_toll || false,
 scope: g.scope || 'organization',
 }))
 .sort((a: any, b: any) => {
 // Tullområden först
 if (a.is_toll && !b.is_toll) return -1;
 if (!a.is_toll && b.is_toll) return 1;
 // Sedan alfabetiskt
 return a.name.localeCompare(b.name, 'sv');
 });
 setGeofences(sorted);
 }
 } catch {
 // Error loading geofences - handled silently
 }
 };

 const loadVehicles = async () => {
 try {
 const response = await api.get('/api/vehicles?limit=10000&includeSubOrgs=true'); // Hämta alla fordon inkl. sub-organisationer
 if (response.data.success) {
 const vehiclesList = response.data.data.map((v: any) => {
 // Vehicles API returnerar: id (som är vehicle_id från databasen), registration_number, etc.
 // Se till att id är en sträng för att matcha event.vehicle_id
 const vehicleId = String(v.id || v.vehicle_id || '');
 const regNumber = v.registration_number || '';
 return {
 id: vehicleId,
 name: regNumber || v.name || v.imei || `${t('geofences.events.vehicleFallback')} ${vehicleId}`,
 registration_number: regNumber,
 };
 });
 setVehicles(vehiclesList);
 }
 } catch {
 // Error loading vehicles - handled silently
 }
 };

 const handleFilterChange = (key: string, value: string) => {
 setFilters({ ...filters, [key]: value });
 setPagination({ ...pagination, page: 1 });
 };

 const clearFilters = () => {
 setFilters({
 vehicle_id: '',
 geofence_id: '',
 event_type: '',
 from_date: '',
 to_date: '',
 });
 setGeofenceSearch('');
 setVehicleSearch('');
 setPagination({ ...pagination, page: 1 });
 };

 const getEventTypeColor = (type: 'enter' | 'exit') => {
 return type === 'enter' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
 };

 const getEventTypeLabel = (type: 'enter' | 'exit') => {
 return type === 'enter' ? t('geofences.events.enter') : t('geofences.events.exit');
 };

 return (
 <div className="p-6 space-y-6">
 <div className="flex justify-between items-center">
 <div>
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <MapPinIcon className="w-6 h-6 text-primary" />
 {t('geofences.events.title')}
 </h1>
 </div>
 <p className="text-sm text-textSecondary mt-1">
 {t('geofences.events.subtitle')}
 </p>
 </div>
 </div>

 {/* Filter */}
 <div className="bg-bgElevated rounded-lg shadow p-4">
 <div className="flex items-center gap-2 mb-4">
 <FunnelIcon className="h-5 w-5 text-textSecondary" />
 <h2 className="text-lg font-semibold text-textDefault">{t('geofences.events.filter')}</h2>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('geofences.events.vehicle')}
 </label>
 <input
 type="text"
 placeholder={t('geofences.events.searchVehicle')}
 value={vehicleSearch}
 onChange={(e) => setVehicleSearch(e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault mb-2"
 />
 <select
 value={filters.vehicle_id}
 onChange={(e) => handleFilterChange('vehicle_id', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('geofences.events.allVehicles')}</option>
 {vehicles
 .filter((v) => 
 !vehicleSearch || 
 (v.registration_number || v.name || '').toLowerCase().includes(vehicleSearch.toLowerCase())
 )
 .map((v) => (
 <option key={v.id} value={v.id}>
 {v.registration_number || v.name}
 </option>
 ))}
 </select>
 {vehicleSearch && (
 <p className="text-xs text-textSecondary mt-1">
 {vehicles.filter((v) => 
 (v.registration_number || v.name || '').toLowerCase().includes(vehicleSearch.toLowerCase())
 ).length} {t('geofences.events.vehiclesMatch')}
 </p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('geofences.events.geofence')}
 </label>
 <input
 type="text"
 placeholder={t('geofences.events.searchGeofence')}
 value={geofenceSearch}
 onChange={(e) => setGeofenceSearch(e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault mb-2"
 />
 <select
 value={filters.geofence_id}
 onChange={(e) => handleFilterChange('geofence_id', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('geofences.events.allGeofences')}</option>
 {geofences
 .filter((g) => 
 !geofenceSearch || 
 g.name.toLowerCase().includes(geofenceSearch.toLowerCase())
 )
 .map((g) => (
 <option key={g.geofence_id} value={g.geofence_id}>
 {g.is_toll ? '💰 ' : ''}{g.name}
 </option>
 ))}
 </select>
 {geofenceSearch && (
 <p className="text-xs text-textSecondary mt-1">
 {geofences.filter((g) => 
 g.name.toLowerCase().includes(geofenceSearch.toLowerCase())
 ).length} {t('geofences.events.geofencesMatch')}
 </p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('geofences.events.eventType')}
 </label>
 <select
 value={filters.event_type}
 onChange={(e) => handleFilterChange('event_type', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('geofences.events.allTypes')}</option>
 <option value="enter">{t('geofences.events.enter')}</option>
 <option value="exit">{t('geofences.events.exit')}</option>
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('geofences.events.fromDate')}
 </label>
 <DateInput value={filters.from_date}
 onChange={(e) => handleFilterChange('from_date', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('geofences.events.toDate')}
 </label>
 <DateInput value={filters.to_date}
 onChange={(e) => handleFilterChange('to_date', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 />
 </div>

 <div className="flex items-end">
 <button
 onClick={clearFilters}
 className="w-full px-4 py-2 bg-bgBase text-textDefault rounded-md hover:bg-bgBase"
 >
 {t('geofences.events.clearFilters')}
 </button>
 </div>
 </div>
 </div>

 {/* Statistik - visar alla events under vald period, inte bara synliga */}
 {pagination.total > 0 && (
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <div className="bg-bgElevated rounded-lg shadow p-4">
 <div className="text-sm text-textSecondary">{t('geofences.events.totalEvents')}</div>
 <div className="text-2xl font-bold text-textDefault mt-1">
 {pagination.total}
 </div>
 </div>
 <div className="bg-bgElevated rounded-lg shadow p-4">
 <div className="text-sm text-textSecondary">{t('geofences.events.enterEvents')}</div>
 <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
 {stats.enterCount}
 </div>
 </div>
 <div className="bg-bgElevated rounded-lg shadow p-4">
 <div className="text-sm text-textSecondary">{t('geofences.events.exitEvents')}</div>
 <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
 {stats.exitCount}
 </div>
 </div>
 <div className="bg-bgElevated rounded-lg shadow p-4">
 <div className="text-sm text-textSecondary">{t('geofences.events.totalCost')}</div>
 <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
 {stats.totalCost.toFixed(2)} SEK
 </div>
 </div>
 </div>
 )}

 {/* Tabell */}
 <div className="bg-bgElevated rounded-lg shadow overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgContainer">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('geofences.events.timeColumn')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('geofences.events.eventColumn')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('geofences.events.geofenceColumn')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('geofences.events.vehicleColumn')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('geofences.events.costColumn')}
 </th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {loading ? (
 <tr>
 <td colSpan={5} className="px-6 py-4 text-center">
 <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-textTertiary" />
 </td>
 </tr>
 ) : events.length === 0 ? (
 <tr>
 <td colSpan={5} className="px-6 py-4 text-center text-textSecondary">
 {t('geofences.events.noEvents')}
 </td>
 </tr>
 ) : (
 events.map((event) => (
 <tr key={event.event_id} className="hover:bg-bgBase">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center text-sm text-textDefault">
 <ClockIcon className="h-4 w-4 mr-2 text-textTertiary" />
 {format(new Date(event.timestamp), 'yyyy-MM-dd HH:mm:ss', { locale: sv })}
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span
 className={`px-2 py-1 text-xs font-semibold rounded-full ${getEventTypeColor(
 event.event_type
 )}`}
 >
 {getEventTypeLabel(event.event_type)}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {event.geofence_name}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {event.vehicle_registration || (() => {
 const eventVehicleId = String(event.vehicle_id || '');
 if (!eventVehicleId || eventVehicleId === 'undefined' || eventVehicleId === 'null') {
 return '-';
 }
 const vehicle = vehicles.find((v) => String(v.id) === eventVehicleId);
 return vehicle?.registration_number || vehicle?.name || '-';
 })()}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {event.cost !== undefined && event.cost !== null ? (
 <div className="flex items-center font-semibold text-blue-600 dark:text-blue-400">
 <CurrencyDollarIcon className="h-4 w-4 mr-1" />
 {event.cost.toFixed(2)} SEK
 </div>
 ) : (
 <span className="text-textTertiary">-</span>
 )}
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Paginering */}
 {pagination.totalPages > 1 && (
 <div className="bg-bgContainer px-6 py-3 flex items-center justify-between">
 <div className="text-sm text-textDefault">
 {t('geofences.events.showing')} {((pagination.page - 1) * pagination.pageSize) + 1} -{' '}
 {Math.min(pagination.page * pagination.pageSize, pagination.total)} {t('geofences.events.of')}{' '}
 {pagination.total} {t('geofences.events.events')}
 </div>
 <div className="flex gap-2">
 <button
 onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
 disabled={pagination.page === 1}
 className="px-3 py-1 border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('common.previous')}
 </button>
 <span className="px-3 py-1 text-sm text-textDefault">
 {t('geofences.events.page')} {pagination.page} {t('geofences.events.of')} {pagination.totalPages}
 </span>
 <button
 onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
 disabled={pagination.page >= pagination.totalPages}
 className="px-3 py-1 border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('common.next')}
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default GeofenceEvents;

